/**
 * PhaseKanbanView - 5-column Kanban view for workflow phases
 * Stitch design system
 */
import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  surface: '#ffffff', surfaceLow: '#f1f4f5',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const PHASES = [
  { id: 'concept', name: 'Concept', color: '#f59e0b', keywords: ['concept', 'brainstorm', 'intake', 'toewijzing', 'debrief'] },
  { id: 'script', name: 'Script', color: '#8b5cf6', keywords: ['script', 'schrijven', 'copywriter', 'storyboard'] },
  { id: 'preproductie', name: 'Pre-productie', color: '#3b82f6', keywords: ['kick-off', 'planning', 'scouten', 'locatie', 'cast', 'props', 'callsheet', 'materiaal', 'ppm', 'draaidag reserv', 'breakdown'] },
  { id: 'productie', name: 'Productie', color: '#10b981', keywords: ['draaidag', 'uitvoering', 'upload', 'formulier', 'aftercare'] },
  { id: 'postproductie', name: 'Post-productie', color: '#ec4899', keywords: ['montage', 'edit', 'animatie', 'color', 'geluid', 'mix', 'review', 'oplevering', 'feedback'] },
];

const ROLE_CONFIG = {
  'Projectleider': { color: '#10b981', abbr: 'PL' },
  'Accountmanager': { color: '#3b82f6', abbr: 'AM' },
  'Regisseur': { color: '#7c3aed', abbr: 'REG' },
  'Camera Operator': { color: '#06b6d4', abbr: 'CAM' },
  'Producer': { color: '#f59e0b', abbr: 'PRO' },
  'Editor': { color: '#3b82f6', abbr: 'ED' },
  'Animator': { color: '#ec4899', abbr: 'AN' },
  'Copywriter': { color: '#8b5cf6', abbr: 'CW' },
  'Productie-assistent': { color: '#f97316', abbr: 'PA' },
  'Management': { color: '#6b7280', abbr: 'MT' },
};

const ROLE_COLORS = Object.fromEntries(Object.entries(ROLE_CONFIG).map(([k, v]) => [k, v.color]));

function getTaskPhase(task) {
  const name = (task.name || '').toLowerCase();
  const phaseName = (task.phase_name || '').toLowerCase();
  const combined = `${phaseName} ${name}`;
  for (const phase of PHASES) {
    if (phase.keywords.some((kw) => combined.includes(kw))) return phase.id;
  }
  return 'preproductie';
}

function PhaseKanbanView({
  tasks = [], conditions = {}, onTaskToggle, showExcluded = true,
  compact = false, useAbbreviations = false, roleFilter = null,
  title = "Taken per fase", hideHeader = false,
}) {
  const getRoleDisplay = (role) => {
    if (!role) return '';
    return useAbbreviations ? (ROLE_CONFIG[role]?.abbr || role.substring(0, 2).toUpperCase()) : role;
  };

  const filteredTasks = roleFilter ? tasks.filter((t) => t.role === roleFilter) : tasks;
  const tasksByPhase = PHASES.reduce((acc, phase) => { acc[phase.id] = []; return acc; }, {});
  filteredTasks.forEach((task) => { const phaseId = getTaskPhase(task); if (tasksByPhase[phaseId]) tasksByPhase[phaseId].push(task); });

  const includedCount = filteredTasks.filter((t) => t.is_included !== false).length;
  const totalCount = filteredTasks.length;

  return (
    <Box>
      {!hideHeader && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {title}
          </Typography>
          <Typography component="span" sx={{
            fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700,
            color: T.primary, bgcolor: T.primaryContainer,
            px: 1.25, py: 0.3, borderRadius: '9999px',
          }}>
            {includedCount}/{totalCount} taken
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: compact ? 1 : 1.5, minHeight: compact ? 150 : 300 }}>
        {PHASES.map((phase) => {
          const phaseTasks = tasksByPhase[phase.id] || [];
          const includedInPhase = phaseTasks.filter((t) => t.is_included !== false).length;

          return (
            <Box key={phase.id} sx={{ borderRadius: '0.75rem', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: T.shadow }}>
              {/* Phase header */}
              <Box sx={{
                bgcolor: phase.color, color: 'white',
                px: compact ? 1 : 1.5, py: compact ? 0.5 : 0.75,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: compact ? '0.6rem' : '0.68rem', fontWeight: 700 }}>
                  {phase.name}
                </Typography>
                <Typography component="span" sx={{
                  fontFamily: T.fontBody, fontSize: compact ? '0.5rem' : '0.55rem', fontWeight: 700,
                  bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                  px: 0.75, py: 0.15, borderRadius: '9999px',
                }}>
                  {includedInPhase}
                </Typography>
              </Box>

              {/* Tasks */}
              <Box sx={{
                flex: 1, p: compact ? 0.5 : 0.75, overflowY: 'auto', bgcolor: T.surfaceLow,
                display: 'flex', flexDirection: 'column', gap: compact ? 0.5 : 0.5,
              }}>
                {phaseTasks.length === 0 ? (
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontStyle: 'italic', color: T.outlineVariant, textAlign: 'center', py: 2 }}>
                    Geen taken
                  </Typography>
                ) : (
                  phaseTasks.map((task, idx) => {
                    const isIncluded = task.is_included !== false;
                    if (!showExcluded && !isIncluded) return null;

                    return (
                      <Tooltip
                        key={task.id || idx}
                        title={
                          <Box>
                            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem' }}>{task.name}</Typography>
                            {task.role && <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', opacity: 0.8 }}>{task.role} • {task.estimated_hours || 0}u</Typography>}
                            {task.conditions?.only_if && <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', mt: 0.5 }}>Conditie: {task.conditions.only_if}</Typography>}
                          </Box>
                        }
                        placement="right" arrow
                        slotProps={{
                          tooltip: { sx: { fontFamily: T.fontBody, bgcolor: T.text, borderRadius: '0.5rem', px: 1.5, py: 0.75 } },
                          arrow: { sx: { color: T.text, '&::before': { border: 'none' } } },
                        }}
                      >
                        <Box
                          onClick={() => onTaskToggle && onTaskToggle(task)}
                          sx={{
                            px: compact ? 0.75 : 1, py: compact ? 0.5 : 0.6,
                            cursor: onTaskToggle ? 'pointer' : 'default',
                            borderRadius: '0.35rem',
                            bgcolor: isIncluded ? T.surface : T.surfaceLow,
                            opacity: isIncluded ? 1 : 0.5,
                            display: 'flex', alignItems: 'flex-start', gap: 0.5,
                            transition: 'all 0.1s',
                            '&:hover': { boxShadow: onTaskToggle ? '0 2px 8px rgba(45,51,53,0.06)' : 'none' },
                          }}
                        >
                          <Icon
                            name={isIncluded ? 'check_circle' : 'remove_circle'}
                            size={compact ? 12 : 14}
                            sx={{ color: isIncluded ? T.emerald : T.outlineVariant, flexShrink: 0, marginTop: 1 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{
                              fontFamily: T.fontBody, fontWeight: 500,
                              fontSize: compact ? '0.6rem' : '0.68rem', lineHeight: 1.3,
                              display: '-webkit-box', WebkitLineClamp: compact ? 1 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                              textDecoration: isIncluded ? 'none' : 'line-through', color: T.text,
                            }}>
                              {(task.name || '').split(' – ')[0].split(' - ')[0]}
                            </Typography>
                            {task.role && (compact ? useAbbreviations : true) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: ROLE_CONFIG[task.role]?.color || T.outlineVariant }} />
                                <Typography sx={{ fontFamily: T.fontBody, fontSize: compact ? '0.5rem' : '0.58rem', color: T.outline }}>
                                  {getRoleDisplay(task.role)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Tooltip>
                    );
                  })
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default PhaseKanbanView;
